// src/components/audio/AudioAudit.js
//
// Audio Audit — full-interview review workspace.
//
// LAYOUT (desktop-only):
//   ┌─────────────────────────────────────────────────────────────┐
//   │ Header: title + audio column selector + project info        │
//   ├─────────────────────────────────────────────────────────────┤
//   │ Submission list (left, ~340px)  │  Player + metadata (right)│
//   │   - Search/filter               │   - Audio file path       │
//   │   - Submissions sorted by date  │   - Big AudioPlayer       │
//   │   - Each row: enumerator,       │   - Submission metadata   │
//   │     firm, date, duration        │     panel                 │
//   └─────────────────────────────────────────────────────────────┘
//
// MEDIA DIR:
//   - On mount, checks project's saved audio_settings.media_dir
//   - If missing/invalid, shows setup prompt
//   - If valid, resolves audio paths for the selected column
//
// AUDIO COLUMN:
//   - Defaults to "audio_audit" (SurveyCTO standard)
//   - Falls back to first audio-shaped column if not present
//   - User can switch via dropdown (lists all columns ending in audio
//     or starting with audio_)
//
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { DataService } from '../../services/dataService';
import AudioPlayer, { resolveAudioPath } from './AudioPlayer';
import { AUDIO_EXT_REGEX } from './audioFormats';
import {
  emptyDocument as emptyNotesDoc,
  addNote as addNoteOp,
  updateNote as updateNoteOp,
  deleteNote as deleteNoteOp,
  setDisposition as setDispositionOp,
  getNotesForPath,
  getDispositionFor,
} from './audioNotes';
import NotesPanel from './NotesPanel';
import DispositionPill from './DispositionPill';
import {
  Headphones, Search, Folder, AlertCircle, FolderOpen,
  ChevronDown, User, Calendar, Building2, Hash, Clock, Languages,
} from 'lucide-react';

// ============================================================================
// HEURISTIC RESOLVERS — columns vary across projects/programmers
// ============================================================================

// Identifier columns (enumerator, firm, date) — listed in priority order.
// The first column that exists in the data is used for that field.
const ID_RESOLVERS = {
  enumerator: [
    'enumerator_name', 'enumerator_id', 'enumerator',
    'enum_name', 'enum_id', 'enums',
    'fo_name', 'fo_id', 'field_officer', 'field_officer_name', 'field_officer_id',
    'data_collector', 'data_collector_name', 'data_collector_id',
    'surveyor', 'surveyor_name', 'surveyor_id',
    'interviewer', 'interviewer_name', 'interviewer_id',
    'username', // SurveyCTO system field, last-resort
  ],
  firm: [
    'firm_name', 'firm_id', 'firm',
    'company_name', 'company_id', 'company',
    'establishment_name', 'establishment_id',
    'business_name', 'business_id',
    'organisation', 'organization', 'org_name',
  ],
  respondent: [
    'respondent_name', 'respondent_id', 'respondent',
    'resp_first_name', 'resp_last_name', 'resp_full_name',
    'participant_name', 'participant_id',
  ],
  submission_id: [
    'KEY', 'key', '_uuid', 'instanceID', 'submission_id', 'submission_uuid',
  ],
  submission_date: [
    'today', 'submission_date', 'starttime', 'SubmissionDate', 'submissiondate', 'date',
  ],
  duration: [
    'duration', 'interview_duration', 'survey_duration',
  ],
};

// Find the first column from a candidate list that actually exists in the data.
const findColumn = (candidateNames, columnNames) => {
  if (!Array.isArray(columnNames)) return null;
  const lowerSet = new Map(columnNames.map(c => [c.toLowerCase(), c]));
  for (const cand of candidateNames) {
    const hit = lowerSet.get(cand.toLowerCase());
    if (hit) return hit;
  }
  return null;
};

// Detect audio columns by SAMPLING CELL VALUES, not column names.
//
// SurveyCTO often produces columns whose names don't contain "audio" or
// "recording" but whose cells hold audio file references — typically because
// the column represents a repeat-group field (bundle_tasks_1, bundle_skills_2,
// etc.) where the form's question type happened to be "audio". Filtering by
// name would miss all of these.
//
// A column is considered an audio column when ≥50% of its non-empty sampled
// cells match the audio extension regex. This catches every legitimate audio
// column regardless of naming convention.
export const detectAudioColumns = (data, columnNames) => {
  if (!Array.isArray(columnNames) || !Array.isArray(data)) return [];
  const sample = data.slice(0, 200);
  const valid = [];
  for (const col of columnNames) {
    let nonEmpty = 0;
    let audioLike = 0;
    for (const row of sample) {
      const v = row && typeof row === 'object' ? row[col] : null;
      if (v != null && String(v).trim()) {
        nonEmpty++;
        if (AUDIO_EXT_REGEX.test(String(v))) audioLike++;
      }
    }
    // Need at least one cell with audio-shaped data to qualify.
    if (nonEmpty > 0 && audioLike / nonEmpty >= 0.5) valid.push(col);
  }
  // Smart sort: audio_audit > audio_test_recording > other audio_* >
  // bundle_roster_audio > other bundle_*audio > everything else alphabetically.
  // Within the bundle_* group, also do natural-number sort so bundle_tasks_2
  // comes before bundle_tasks_10.
  const tier = (name) => {
    const n = name.toLowerCase();
    if (n === 'audio_audit') return 0;
    if (n === 'audio_test_recording') return 1;
    if (n.startsWith('audio_') || n.endsWith('_audio')) return 2;
    if (n === 'bundle_roster_audio') return 3;
    if (n.startsWith('bundle_')) return 4;
    return 5;
  };
  const naturalKey = (s) => s.replace(/(\d+)/g, (m) => m.padStart(8, '0'));
  valid.sort((a, b) => {
    const ta = tier(a), tb = tier(b);
    if (ta !== tb) return ta - tb;
    return naturalKey(a.toLowerCase()).localeCompare(naturalKey(b.toLowerCase()));
  });
  return valid;
};

// Group audio columns by their leading prefix for the dropdown.
// Returns an array of { groupLabel, columns: [...] } for rendering as
// labeled sections. A column with no obvious group (e.g. audio_audit) goes
// into a single-item group with its own name as the label.
export const groupAudioColumns = (audioColumns) => {
  if (!Array.isArray(audioColumns) || audioColumns.length === 0) return [];
  // Determine the prefix for each column: bundle_tasks_1 -> bundle_tasks,
  // audio_audit -> audio_audit, audio_test_recording -> audio_test_recording.
  const groupOf = (name) => {
    // If name ends in _<digits>, strip them — that's the repeat-group suffix.
    return name.replace(/_(\d+)$/i, '');
  };
  const seen = new Map(); // groupName -> columns[]
  for (const col of audioColumns) {
    const g = groupOf(col);
    if (!seen.has(g)) seen.set(g, []);
    seen.get(g).push(col);
  }
  // Preserve the input order (already smart-sorted upstream)
  const result = [];
  for (const [groupName, cols] of seen.entries()) {
    result.push({ groupLabel: groupName, columns: cols });
  }
  return result;
};

// Format ISO/date-ish strings to a friendly short date
const formatDate = (raw) => {
  if (!raw) return '';
  const s = String(raw);
  // Already short like "4/29/2026"
  if (/^\d+[\/\-]\d+[\/\-]\d+/.test(s) && s.length < 12) return s;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString();
  }
  return s.slice(0, 16);
};

// Convert ms or seconds duration into mm:ss
const formatDuration = (raw) => {
  if (raw == null || raw === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  // SurveyCTO `duration` is in seconds; if value is unrealistically large for an interview,
  // assume milliseconds.
  const seconds = n > 100000 ? Math.floor(n / 1000) : Math.floor(n);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// ── Tauri API loader (ES modules, matches DataLoader pattern) ──────────────
let _tauriApiCache = null;
const getTauriApi = () => {
  if (!(typeof window !== 'undefined' && window.__TAURI_INTERNALS__)) return null;
  if (_tauriApiCache) return _tauriApiCache;
  return (async () => {
    try {
      const [{ invoke }, { listen }] = await Promise.all([
        import('@tauri-apps/api/core'),
        import('@tauri-apps/api/event'),
      ]);
      _tauriApiCache = { invoke, listen };
      return _tauriApiCache;
    } catch (e) {
      console.error('Failed to load Tauri API:', e);
      return null;
    }
  })();
};

const AudioAudit = ({ projectId, primaryData, isTauri, onNavigate, userInfo }) => {
  const { isDarkMode } = useTheme();
  const colors = useColors(isDarkMode);

  // ── Resolve Tauri API on mount ────────────────────────────────────────────
  const [tauriApi, setTauriApi] = useState(null);
  useEffect(() => {
    if (!isTauri) return;
    let mounted = true;
    (async () => {
      const api = await getTauriApi();
      if (mounted) setTauriApi(api);
    })();
    return () => { mounted = false; };
  }, [isTauri]);

  // ── Project-level audio settings (mediaDir + audio columns) ──────────────
  const [audioSettings, setAudioSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoadingSettings(true);
      try {
        const settings = await DataService.getAudioSettings(projectId);
        if (!cancelled) setAudioSettings(settings || { media_dir: null });
      } catch (e) {
        // Endpoint may not exist yet; default to empty settings
        if (!cancelled) setAudioSettings({ media_dir: null });
      } finally {
        if (!cancelled) setLoadingSettings(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // ── Reviewer notes + dispositions document ───────────────────────────────
  // Single document per project, holding all notes + the disposition map.
  // Loaded once on mount; mutations update local state then PUT the whole
  // payload (matches audio_settings / cleaning_state pattern).

  // Refs that always reflect the current selection — see effect later that
  // keeps them in sync. Used by note handlers so their closures stay stable.
  const currentAudioAbsPathRef = useRef(null);
  const currentAudioColumnRef  = useRef(null);
  const currentSubmissionIdRef = useRef(null);
  const currentPlayTimeRef     = useRef(0);

  const [notesDoc, setNotesDoc] = useState(emptyNotesDoc());
  const [notesSaving, setNotesSaving] = useState(false);
  const notesSaveQueueRef = useRef(null); // hold the latest doc to save

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const doc = await DataService.getAudioNotes(projectId);
        if (!cancelled) setNotesDoc(doc || emptyNotesDoc());
      } catch (e) {
        if (!cancelled) setNotesDoc(emptyNotesDoc());
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Persist the current document to backend. We coalesce rapid mutations:
  // if a save is in flight, we stash the latest doc and re-save when it
  // completes. Last write wins.
  const persistNotesDoc = useCallback(async (nextDoc) => {
    if (!projectId) return;
    if (notesSaving) {
      notesSaveQueueRef.current = nextDoc;
      return;
    }
    setNotesSaving(true);
    try {
      await DataService.saveAudioNotes(projectId, nextDoc);
    } catch (e) {
      console.warn('Failed to save audio notes:', e);
    } finally {
      setNotesSaving(false);
      // If another mutation arrived during the save, fire it now
      const pending = notesSaveQueueRef.current;
      notesSaveQueueRef.current = null;
      if (pending) persistNotesDoc(pending);
    }
  // eslint-disable-next-line
  }, [projectId, notesSaving]);

  const author = userInfo?.name || userInfo?.email || null;

  const handleAddNote = useCallback(async (text, tag, timestamp) => {
    const next = addNoteOp(notesDoc, {
      audio_path:        currentAudioAbsPathRef.current,
      audio_column:      currentAudioColumnRef.current,
      submission_id:     currentSubmissionIdRef.current,
      timestamp_seconds: timestamp,
      text, tag, author,
    });
    setNotesDoc(next);
    await persistNotesDoc(next);
  }, [notesDoc, persistNotesDoc, author]);

  const handleUpdateNote = useCallback(async (noteId, patch) => {
    const next = updateNoteOp(notesDoc, noteId, patch);
    setNotesDoc(next);
    await persistNotesDoc(next);
  }, [notesDoc, persistNotesDoc]);

  const handleDeleteNote = useCallback(async (noteId) => {
    const next = deleteNoteOp(notesDoc, noteId);
    setNotesDoc(next);
    await persistNotesDoc(next);
  }, [notesDoc, persistNotesDoc]);

  const handleSetDisposition = useCallback(async (dispId) => {
    const next = setDispositionOp(
      notesDoc,
      currentSubmissionIdRef.current,
      currentAudioColumnRef.current,
      dispId,
    );
    setNotesDoc(next);
    await persistNotesDoc(next);
  }, [notesDoc, persistNotesDoc]);

  // ── Player seek coordination ─────────────────────────────────────────────
  // Used by NotesPanel to jump the player to a note's timestamp. AudioPlayer
  // re-runs its seek effect whenever seekRequest.key changes (so clicking the
  // same note twice still re-seeks).
  const [seekRequest, setSeekRequest] = useState(null);
  const handleJumpTo = useCallback((seconds) => {
    setSeekRequest({ to: seconds, key: Date.now() });
  }, []);

  // The NotesPanel's "Add at <time>" button needs to know the current playback
  // position. We mirror it into a ref + a state value (state for the UI, ref
  // for the stable handler).
  const [playTime, setPlayTime] = useState(0);
  const handleTimeChange = useCallback((t) => {
    currentPlayTimeRef.current = t;
    setPlayTime(t);
  }, []);


  // ── Resolve identifier columns ────────────────────────────────────────────
  const columnNames = useMemo(() => {
    if (!primaryData?.columns) return [];
    return primaryData.columns.map((c) =>
      typeof c === 'string' ? c : (c?.name || c?.column || '')
    ).filter(Boolean);
  }, [primaryData]);

  const idCols = useMemo(() => ({
    enumerator:      findColumn(ID_RESOLVERS.enumerator, columnNames),
    firm:            findColumn(ID_RESOLVERS.firm, columnNames),
    respondent:      findColumn(ID_RESOLVERS.respondent, columnNames),
    submission_id:   findColumn(ID_RESOLVERS.submission_id, columnNames),
    submission_date: findColumn(ID_RESOLVERS.submission_date, columnNames),
    duration:        findColumn(ID_RESOLVERS.duration, columnNames),
  }), [columnNames]);

  // ── Resolve audio columns ─────────────────────────────────────────────────
  const audioColumns = useMemo(() => {
    return detectAudioColumns(primaryData?.data || [], columnNames);
  }, [primaryData, columnNames]);

  const [activeAudioColumn, setActiveAudioColumn] = useState(null);
  useEffect(() => {
    if (activeAudioColumn) return;
    if (audioColumns.length === 0) return;
    setActiveAudioColumn(audioColumns[0]);
  }, [audioColumns, activeAudioColumn]);

  // ── Build submission list ─────────────────────────────────────────────────
  const submissions = useMemo(() => {
    if (!primaryData?.data || !activeAudioColumn) return [];
    const rows = primaryData.data;
    return rows.map((row, idx) => {
      if (!row || typeof row !== 'object') return null;
      const audioFile = row[activeAudioColumn];
      return {
        index: idx,
        audioFile: audioFile ? String(audioFile) : '',
        enumerator: idCols.enumerator ? row[idCols.enumerator] : '',
        firm:       idCols.firm ? row[idCols.firm] : '',
        respondent: idCols.respondent ? row[idCols.respondent] : '',
        submissionId: idCols.submission_id ? row[idCols.submission_id] : `Row ${idx + 1}`,
        submissionDate: idCols.submission_date ? row[idCols.submission_date] : '',
        duration: idCols.duration ? row[idCols.duration] : '',
        rawRow: row,
      };
    }).filter(Boolean);
  }, [primaryData, activeAudioColumn, idCols]);

  // ── Search filter ─────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const filteredSubmissions = useMemo(() => {
    if (!search.trim()) return submissions;
    const q = search.toLowerCase();
    return submissions.filter((s) => {
      const hay = [
        s.audioFile, s.enumerator, s.firm, s.respondent, s.submissionId,
      ].map((v) => String(v || '').toLowerCase()).join(' ');
      return hay.includes(q);
    });
  }, [submissions, search]);

  // ── Selected submission ───────────────────────────────────────────────────
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selected = filteredSubmissions[selectedIdx] || filteredSubmissions[0] || null;
  // Reset selection when filter changes substantially
  useEffect(() => {
    setSelectedIdx(0);
  }, [search, activeAudioColumn]);

  // ── Audio path resolution + missing-file detection ────────────────────────
  const audioAbsPath = useMemo(() => {
    if (!audioSettings?.media_dir || !selected?.audioFile) return null;
    return resolveAudioPath(audioSettings.media_dir, selected.audioFile);
  }, [audioSettings, selected]);

  // Keep selection refs in sync so the stable note handlers can read current values
  useEffect(() => {
    currentAudioAbsPathRef.current = audioAbsPath;
    currentAudioColumnRef.current  = activeAudioColumn;
    currentSubmissionIdRef.current = selected?.submissionId || null;
  }, [audioAbsPath, activeAudioColumn, selected]);

  // Reset play time when the audio file changes — otherwise stale time
  // shows in the "Add at" button momentarily.
  useEffect(() => {
    setPlayTime(0);
    currentPlayTimeRef.current = 0;
  }, [audioAbsPath]);

  // Notes for the currently-selected audio file
  const currentNotes = useMemo(() => {
    if (!audioAbsPath) return [];
    return getNotesForPath(notesDoc, audioAbsPath);
  }, [notesDoc, audioAbsPath]);

  // Disposition for the currently-selected submission + column
  const currentDisposition = useMemo(() => {
    return getDispositionFor(notesDoc, selected?.submissionId, activeAudioColumn);
  }, [notesDoc, selected, activeAudioColumn]);

  // ── Per-file duration loading (Option A) ──────────────────────────────────
  // For each submission with an audio file in the active column, read the
  // file's actual playback duration via HTML5 <audio> metadata. This gives
  // the playlist accurate durations per audio column — bundle_tasks_1
  // recordings might be 12 seconds while audio_audit is 58 minutes.
  //
  // Implementation notes:
  //  - Bytes are read via the same Tauri command as the player, then wrapped
  //    in a blob URL and fed to a hidden <audio> element with preload=metadata
  //  - Each load is independent; failures don't block the rest
  //  - The map is keyed by the full audio path so it survives column switches
  //    and revisits cheaply (cache warms over the session)
  //  - Concurrency is capped to keep file IO sane on slower disks
  const [fileDurations, setFileDurations] = useState({});  // { path: seconds | null }
  const fileDurationsRef = useRef(fileDurations);
  fileDurationsRef.current = fileDurations;

  useEffect(() => {
    if (!tauriApi?.invoke || !audioSettings?.media_dir) return;
    if (!filteredSubmissions || filteredSubmissions.length === 0) return;

    // Collect paths that need loading (skip ones we already have)
    const todo = [];
    for (const sub of filteredSubmissions) {
      if (!sub.audioFile) continue;
      const abs = resolveAudioPath(audioSettings.media_dir, sub.audioFile);
      if (!abs) continue;
      if (fileDurationsRef.current[abs] !== undefined) continue;
      todo.push(abs);
    }
    if (todo.length === 0) return;

    let cancelled = false;
    const blobUrlsToRevoke = [];

    // Load with a small concurrency cap — 4 in flight at once.
    const loadOne = async (path) => {
      try {
        const bytes = await tauriApi.invoke('read_file_bytes', { path });
        if (cancelled) return;
        let view;
        if (bytes instanceof Uint8Array) view = bytes;
        else if (bytes instanceof ArrayBuffer) view = new Uint8Array(bytes);
        else if (Array.isArray(bytes)) view = new Uint8Array(bytes);
        else throw new Error('Unexpected byte format');

        const blob = new Blob([view]);
        const url = URL.createObjectURL(blob);
        blobUrlsToRevoke.push(url);

        const seconds = await new Promise((resolve) => {
          const a = document.createElement('audio');
          a.preload = 'metadata';
          a.muted = true;
          let settled = false;
          const finish = (val) => {
            if (settled) return;
            settled = true;
            resolve(val);
          };
          a.onloadedmetadata = () => finish(Number.isFinite(a.duration) ? a.duration : null);
          a.onerror = () => finish(null);
          // Timeout safeguard: if metadata never fires, give up after 8s
          setTimeout(() => finish(null), 8000);
          a.src = url;
        });

        if (cancelled) return;
        setFileDurations((prev) => ({ ...prev, [path]: seconds }));
      } catch (e) {
        if (cancelled) return;
        // Mark as null so we don't retry endlessly
        setFileDurations((prev) => ({ ...prev, [path]: null }));
      }
    };

    // Process in batches of 4
    (async () => {
      const queue = [...todo];
      const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
        while (queue.length > 0 && !cancelled) {
          const next = queue.shift();
          if (next) await loadOne(next);
        }
      });
      await Promise.all(workers);
    })();

    return () => {
      cancelled = true;
      // Revoke any blob URLs we made — even if cancelled mid-flight,
      // we still want to free the memory.
      for (const url of blobUrlsToRevoke) {
        URL.revokeObjectURL(url);
      }
    };
    // We deliberately don't depend on fileDurations itself, only its keys
    // via the ref pattern above — otherwise this effect would re-run after
    // every map update and never terminate.
  // eslint-disable-next-line
  }, [filteredSubmissions, audioSettings, tauriApi, activeAudioColumn]);

  // ── Pick media folder ─────────────────────────────────────────────────────
  const handlePickMediaDir = useCallback(async () => {
    if (!tauriApi?.invoke) return;
    try {
      const path = await tauriApi.invoke('pick_folder', {
        title: 'Choose your project media folder',
      });
      if (!path) return;
      setSavingSettings(true);
      const next = { ...(audioSettings || {}), media_dir: path };
      setAudioSettings(next);
      try {
        await DataService.saveAudioSettings(projectId, next);
      } catch (e) {
        console.warn('Could not persist audio settings to backend:', e);
        // Still keep in memory for this session
      }
      setSavingSettings(false);
    } catch (e) {
      console.error('Folder picker failed:', e);
    }
  }, [tauriApi, projectId, audioSettings]);

  // ── Audio language hint (Ship 2) ──────────────────────────────────────────
  // Project-level default language for transcription. Whisper uses this as
  // its `--language` flag during the full transcribe job (TranscribeModal
  // reads audioSettings.audio_language). Per-row re-transcribe overrides
  // this at the row level.
  const handleSetAudioLanguage = useCallback(async (langCode) => {
    if (!projectId) return;
    setSavingSettings(true);
    const next = { ...(audioSettings || {}), audio_language: langCode };
    setAudioSettings(next);
    try {
      await DataService.saveAudioSettings(projectId, next);
    } catch (e) {
      console.warn('Could not persist audio language:', e);
    }
    setSavingSettings(false);
  }, [projectId, audioSettings]);

  // ── Tauri-only feature gate ───────────────────────────────────────────────
  if (!isTauri) {
    return (
      <FeatureGateNotDesktop colors={colors} onNavigate={onNavigate} />
    );
  }

  // ── No data loaded yet ────────────────────────────────────────────────────
  if (!primaryData) {
    return (
      <EmptyState
        colors={colors}
        icon={<Headphones size={32} />}
        title="No project data loaded"
        body="Load primary data first to use the Audio Audit workspace."
        action={{ label: 'Go to Data Sources', onClick: () => onNavigate?.('data-source') }}
      />
    );
  }

  // ── No audio columns detected ─────────────────────────────────────────────
  if (audioColumns.length === 0 && !loadingSettings) {
    return (
      <EmptyState
        colors={colors}
        icon={<Headphones size={32} />}
        title="No audio columns detected in this project"
        body="The Audio Audit workspace expects columns containing audio file references (e.g. audio_audit, response audio columns). None were found in the loaded primary data."
      />
    );
  }

  // ── Media folder not set ──────────────────────────────────────────────────
  if (!audioSettings?.media_dir && !loadingSettings) {
    return (
      <MediaDirPrompt colors={colors} onPickFolder={handlePickMediaDir} saving={savingSettings} />
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div style={{
      maxWidth: '1280px', margin: '0 auto',
      padding: '24px',
      fontFamily: "'Outfit', system-ui, -apple-system, sans-serif",
      color: colors.text,
    }}>
      {/* Header */}
      <Header
        colors={colors}
        audioColumns={audioColumns}
        activeAudioColumn={activeAudioColumn}
        onSelectColumn={setActiveAudioColumn}
        mediaDir={audioSettings?.media_dir}
        onChangeMediaDir={handlePickMediaDir}
        audioLanguage={audioSettings?.audio_language}
        onChangeAudioLanguage={handleSetAudioLanguage}
        submissionCount={submissions.length}
      />

      {/* Two-pane layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '340px 1fr',
        gap: '20px',
        marginTop: '20px',
      }}>
        {/* Left: submission list */}
        <SubmissionList
          colors={colors}
          submissions={filteredSubmissions}
          selectedIdx={selectedIdx}
          onSelect={setSelectedIdx}
          search={search}
          setSearch={setSearch}
          totalCount={submissions.length}
          fileDurations={fileDurations}
          mediaDir={audioSettings?.media_dir}
        />

        {/* Right: player + metadata */}
        <div style={{ minWidth: 0 }}>
          {selected ? (
            <PlayerPane
              colors={colors}
              isDarkMode={isDarkMode}
              submission={selected}
              audioPath={audioAbsPath}
              tauriApi={tauriApi}
              idCols={idCols}
              // Notes / disposition wiring
              currentNotes={currentNotes}
              currentDisposition={currentDisposition}
              currentPlayTime={playTime}
              onAddNote={handleAddNote}
              onUpdateNote={handleUpdateNote}
              onDeleteNote={handleDeleteNote}
              onSetDisposition={handleSetDisposition}
              onJumpTo={handleJumpTo}
              onTimeChange={handleTimeChange}
              seekRequest={seekRequest}
              notesSaving={notesSaving}
            />
          ) : (
            <div style={{
              padding: '40px',
              background: colors.cellBg,
              border: `1px solid ${colors.cellBdr}`,
              borderRadius: '12px',
              textAlign: 'center',
              color: colors.textMuted,
              fontSize: '12.5px',
            }}>
              No submissions match your search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Project-level language hint for Whisper transcription. Whisper supports
// English well and a long tail of other languages with varying accuracy.
// For Zambian fieldwork, the realistic options are English (default) or
// Auto-detect for mixed-language datasets.
//
// Earlier iterations exposed Nyanja, Swahili, and Shona as "experimental"
// options. Field testing showed two problems:
//   1. Whisper rejects ISO codes like 'ny' (Nyanja) outright — they aren't
//      in its training language set, so the request errored before any
//      audio was processed.
//   2. When forced through auto-detect, Whisper produced confident-looking
//      but wholly fabricated transcripts in unrelated languages (e.g.
//      Tagalog tokens for Bemba audio). This is worse than no transcript
//      at all because reviewers can't distinguish real vs. hallucinated
//      output.
//
// Until Whisper (or a fine-tuned variant) can handle these reliably, we
// limit the picker to English + Auto-detect.
const AUDIO_LANGUAGES = [
  { code: 'en',   label: 'English',     reliable: true  },
  { code: 'auto', label: 'Auto-detect', reliable: true  },
];

const Header = ({ colors, audioColumns, activeAudioColumn, onSelectColumn, mediaDir, onChangeMediaDir, audioLanguage, onChangeAudioLanguage, submissionCount }) => {
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langRef = useRef(null);
  useEffect(() => {
    if (!langMenuOpen) return;
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langMenuOpen]);

  const currentLang = audioLanguage || 'en';
  const currentLangLabel =
    AUDIO_LANGUAGES.find(l => l.code === currentLang)?.label || currentLang;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start',
      justifyContent: 'space-between', gap: '20px',
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '10px',
            background: colors.accentSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: colors.accent,
          }}>
            <Headphones size={20} />
          </div>
          <div>
            <h1 style={{
              margin: 0, fontSize: '22px', fontWeight: 600,
              color: colors.text, letterSpacing: '-0.02em',
            }}>
              Audio Audit
            </h1>
            <div style={{ fontSize: '12.5px', color: colors.textMuted, marginTop: '2px' }}>
              Review interview recordings · {submissionCount} submission{submissionCount !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', flexShrink: 0, alignItems: 'center' }}>
        {/* Audio column selector */}
        {audioColumns.length > 1 && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setColMenuOpen(o => !o)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '8px 12px',
                background: colors.cellBg,
                border: `1px solid ${colors.cellBdr}`,
                borderRadius: '8px',
                color: colors.text,
                fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <span style={{ color: colors.textMuted }}>Audio:</span>
              <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                {activeAudioColumn || '—'}
              </span>
              <ChevronDown size={13} style={{ color: colors.textMuted }} />
            </button>
            {colMenuOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                background: colors.modalBg,
                border: `1px solid ${colors.cellBdr}`,
                borderRadius: '8px',
                boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
                minWidth: '260px',
                maxWidth: '380px',
                maxHeight: '440px',
                padding: '4px',
                zIndex: 50,
                overflowY: 'auto',
              }}>
                {groupAudioColumns(audioColumns).map((group, groupIdx) => (
                  <div key={group.groupLabel}>
                    {/* Group divider — skip the first one */}
                    {groupIdx > 0 && (
                      <div style={{
                        margin: '4px 8px',
                        height: '1px',
                        background: colors.cellBdr,
                      }} />
                    )}
                    {/* Group label only when the group has >1 column
                        (otherwise the column name itself is the label) */}
                    {group.columns.length > 1 && (
                      <div style={{
                        padding: '6px 10px 3px',
                        fontSize: '10.5px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: colors.textMuted,
                        fontFamily: "'Outfit', system-ui, sans-serif",
                      }}>
                        {group.groupLabel} <span style={{
                          opacity: 0.6, fontWeight: 400,
                        }}>({group.columns.length})</span>
                      </div>
                    )}
                    {group.columns.map((col) => (
                      <button
                        key={col}
                        onClick={() => { onSelectColumn(col); setColMenuOpen(false); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '7px 10px',
                          background: col === activeAudioColumn ? colors.accentSoft : 'transparent',
                          border: 'none', borderRadius: '5px',
                          color: col === activeAudioColumn ? colors.accent : colors.text,
                          fontWeight: col === activeAudioColumn ? 600 : 400,
                          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                          fontSize: '12.5px',
                          cursor: 'pointer',
                        }}
                      >
                        {col}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Audio language pill (Ship 2) */}
        <div ref={langRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setLangMenuOpen(o => !o)}
            title={`Default Whisper language: ${currentLangLabel}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '8px 12px',
              background: colors.cellBg,
              border: `1px solid ${colors.cellBdr}`,
              borderRadius: '8px',
              color: colors.text,
              fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <Languages size={13} style={{ color: colors.textMuted, flexShrink: 0 }} />
            <span style={{ color: colors.textMuted }}>Lang:</span>
            <span>{currentLangLabel}</span>
            <ChevronDown size={13} style={{ color: colors.textMuted }} />
          </button>
          {langMenuOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', right: 0,
              background: colors.modalBg,
              border: `1px solid ${colors.cellBdr}`,
              borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              padding: '6px',
              minWidth: '200px',
              zIndex: 50,
            }}>
              <div style={{
                fontSize: '10.5px', fontWeight: 600,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: colors.textMuted,
                padding: '4px 8px 6px',
              }}>
                Default Whisper language
              </div>
              {AUDIO_LANGUAGES.map(l => (
                <button
                  key={l.code}
                  onClick={() => { onChangeAudioLanguage?.(l.code); setLangMenuOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '6px 8px',
                    background: l.code === currentLang ? colors.accentSoft : 'transparent',
                    border: 'none',
                    borderRadius: '5px',
                    color: colors.text,
                    fontFamily: 'inherit', fontSize: '12.5px',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { if (l.code !== currentLang) e.currentTarget.style.background = colors.btnBgHover; }}
                  onMouseLeave={(e) => { if (l.code !== currentLang) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{l.label}</span>
                  {!l.reliable && (
                    <span title="Whisper has limited training data for this language; output quality may vary"
                          style={{ fontSize: '10.5px', color: colors.textMuted }}>
                      experimental
                    </span>
                  )}
                </button>
              ))}
              <div style={{
                fontSize: '10.5px', color: colors.textMuted,
                padding: '6px 8px 4px', lineHeight: 1.4,
                borderTop: `1px solid ${colors.cellBdr}`,
                marginTop: '4px',
              }}>
                Used by full transcribe jobs. Per-row re-transcribe overrides this.
              </div>
            </div>
          )}
        </div>
        {/* Media folder pill */}
        <button
          onClick={onChangeMediaDir}
          title={mediaDir}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px',
            background: colors.cellBg,
            border: `1px solid ${colors.cellBdr}`,
            borderRadius: '8px',
            color: colors.textSec,
            fontFamily: 'inherit', fontSize: '12.5px',
            cursor: 'pointer',
            maxWidth: '300px',
          }}
        >
          <FolderOpen size={13} style={{ color: colors.textMuted, flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {mediaDir}
          </span>
        </button>
      </div>
    </div>
  );
};

const SubmissionList = ({ colors, submissions, selectedIdx, onSelect, search, setSearch, totalCount, fileDurations, mediaDir }) => (
  <div style={{
    background: colors.cellBg,
    border: `1px solid ${colors.cellBdr}`,
    borderRadius: '12px',
    overflow: 'hidden',
    maxHeight: 'calc(100vh - 220px)',
    display: 'flex', flexDirection: 'column',
  }}>
    {/* Search */}
    <div style={{
      padding: '12px',
      borderBottom: `1px solid ${colors.cellBdr}`,
      position: 'relative',
    }}>
      <Search size={13} style={{
        position: 'absolute', left: '22px', top: '50%', transform: 'translateY(-50%)',
        color: colors.textMuted, pointerEvents: 'none',
      }} />
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={`Search ${totalCount} submission${totalCount !== 1 ? 's' : ''}...`}
        style={{
          width: '100%',
          padding: '8px 10px 8px 32px',
          background: colors.inputBg,
          border: `1px solid ${colors.cellBdr}`,
          borderRadius: '8px',
          color: colors.text,
          fontSize: '12.5px',
          fontFamily: 'inherit',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </div>
    {/* Items */}
    <div style={{ overflowY: 'auto', flex: 1 }}>
      {submissions.length === 0 && (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.textMuted, fontSize: '12.5px' }}>
          No matches.
        </div>
      )}
      {submissions.map((sub, idx) => {
        const active = idx === selectedIdx;
        // Resolve absolute path the same way the player does, so we can
        // look up the file's duration in the durations map.
        const absPath = sub.audioFile && mediaDir
          ? resolveAudioPath(mediaDir, sub.audioFile)
          : null;
        const fileDur = absPath != null && fileDurations ? fileDurations[absPath] : undefined;
        // States: undefined = not loaded yet (show animated ...);
        //         null      = load failed / no audio (omit duration);
        //         number    = loaded, show formatted seconds.
        const durationLabel = (() => {
          if (!sub.audioFile) return null; // no audio in this column for this row
          if (fileDur === undefined) return '…';
          if (fileDur === null) return null;
          return formatDuration(fileDur);
        })();
        return (
          <button
            key={`${sub.submissionId}-${idx}`}
            onClick={() => onSelect(idx)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '11px 14px',
              background: active ? colors.accentSoft : 'transparent',
              border: 'none',
              borderLeft: active ? `3px solid ${colors.accent}` : '3px solid transparent',
              borderBottom: `1px solid ${colors.cellBdr}`,
              cursor: 'pointer',
              transition: 'background 0.1s',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = colors.btnBgHover; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{
              fontSize: '12.5px', fontWeight: 600,
              color: active ? colors.accent : colors.text,
              marginBottom: '3px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {sub.enumerator || '(no enumerator)'}
            </div>
            <div style={{
              fontSize: '11.5px', color: colors.textSec,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {sub.firm || '(no firm)'}
            </div>
            <div style={{
              fontSize: '10.5px', color: colors.textMuted,
              marginTop: '4px',
              display: 'flex', gap: '10px',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {sub.submissionDate && <span>{formatDate(sub.submissionDate)}</span>}
              {durationLabel && <span>· {durationLabel}</span>}
              {!sub.audioFile && <span style={{ opacity: 0.6 }}>· no audio</span>}
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const PlayerPane = ({
  colors, isDarkMode, submission, audioPath, tauriApi, idCols,
  // Notes / disposition
  currentNotes, currentDisposition, currentPlayTime,
  onAddNote, onUpdateNote, onDeleteNote, onSetDisposition,
  onJumpTo, onTimeChange, seekRequest, notesSaving,
}) => {
  const meta = [
    { icon: <User size={13} />,      label: idCols.enumerator      || 'Enumerator',      value: submission.enumerator },
    { icon: <Building2 size={13} />, label: idCols.firm            || 'Firm',            value: submission.firm },
    { icon: <User size={13} />,      label: idCols.respondent      || 'Respondent',      value: submission.respondent },
    { icon: <Calendar size={13} />,  label: idCols.submission_date || 'Date',            value: formatDate(submission.submissionDate) },
    { icon: <Clock size={13} />,     label: 'Survey duration',                              value: formatDuration(submission.duration) },
    { icon: <Hash size={13} />,      label: idCols.submission_id   || 'Submission',      value: submission.submissionId },
  ].filter(m => m.value !== '' && m.value != null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <AudioPlayer
        audioPath={audioPath}
        filename={submission.audioFile}
        tauriApi={tauriApi}
        keyboardScope={true}
        onTimeChange={onTimeChange}
        seekRequest={seekRequest}
      />

      {/* Disposition — prominent, banded above the rest */}
      <DispositionPill
        value={currentDisposition}
        onChange={onSetDisposition}
        isDarkMode={isDarkMode}
        saving={notesSaving}
      />

      {/* Notes panel */}
      <NotesPanel
        notes={currentNotes}
        currentTime={currentPlayTime}
        onAdd={onAddNote}
        onUpdate={onUpdateNote}
        onDelete={onDeleteNote}
        onJumpTo={onJumpTo}
        isDarkMode={isDarkMode}
        disabled={!audioPath}
      />

      {/* Metadata grid */}
      <div style={{
        background: colors.cellBg,
        border: `1px solid ${colors.cellBdr}`,
        borderRadius: '12px',
        padding: '16px 18px',
      }}>
        <div style={{
          fontSize: '10.5px', color: colors.textMuted, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.1em',
          marginBottom: '10px',
        }}>
          Submission details
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '10px 18px',
        }}>
          {meta.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0 }}>
              <div style={{
                width: '24px', height: '24px', borderRadius: '6px',
                background: colors.btnBg,
                color: colors.textMuted,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {m.icon}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '10.5px', color: colors.textMuted, fontWeight: 500, letterSpacing: '0.02em' }}>
                  {m.label}
                </div>
                <div style={{
                  fontSize: '12.5px', color: colors.text, fontWeight: 500, marginTop: '1px',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {m.value || '—'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MediaDirPrompt = ({ colors, onPickFolder, saving }) => (
  <div style={{
    maxWidth: '520px', margin: '60px auto',
    padding: '32px',
    background: colors.cellBg,
    border: `1px solid ${colors.cellBdr}`,
    borderRadius: '14px',
    textAlign: 'center',
    fontFamily: "'Outfit', system-ui, sans-serif",
  }}>
    <div style={{
      width: '52px', height: '52px', borderRadius: '50%',
      background: colors.accentSoft,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      color: colors.accent, marginBottom: '14px',
    }}>
      <Folder size={24} />
    </div>
    <h2 style={{
      margin: 0, fontSize: '18px', fontWeight: 600,
      color: colors.text, letterSpacing: '-0.01em', marginBottom: '8px',
    }}>
      Set the project media folder
    </h2>
    <p style={{
      fontSize: '13px', color: colors.textSec, lineHeight: 1.6,
      maxWidth: '380px', margin: '0 auto 20px',
    }}>
      Audio Audit needs to know where your interview recordings live on disk.
      Pick the folder containing your <code style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        background: colors.btnBg, padding: '1px 5px', borderRadius: '3px',
      }}>media\</code> files.
      We'll remember this folder for the project.
    </p>
    <button
      onClick={onPickFolder}
      disabled={saving}
      style={{
        padding: '10px 18px',
        background: `linear-gradient(135deg, ${colors.accentDeep || colors.accent}, ${colors.accent})`,
        color: '#FFFFFF', border: 'none', borderRadius: '9px',
        fontSize: '13px', fontWeight: 600,
        fontFamily: 'inherit',
        cursor: saving ? 'wait' : 'pointer',
        opacity: saving ? 0.6 : 1,
        boxShadow: `0 4px 14px ${colors.accent}33`,
      }}
    >
      {saving ? 'Saving...' : 'Choose folder...'}
    </button>
  </div>
);

const FeatureGateNotDesktop = ({ colors, onNavigate }) => (
  <EmptyState
    colors={colors}
    icon={<Headphones size={32} />}
    title="Audio Audit requires DataQuad Desktop"
    body="Audio playback runs locally to keep your recordings on your machine. Download the desktop app to use this workspace."
    action={{ label: 'Download', onClick: () => onNavigate?.('download') }}
  />
);

const EmptyState = ({ colors, icon, title, body, action }) => (
  <div style={{
    maxWidth: '480px', margin: '60px auto',
    padding: '40px 32px',
    textAlign: 'center',
    fontFamily: "'Outfit', system-ui, sans-serif",
  }}>
    <div style={{
      width: '56px', height: '56px', borderRadius: '50%',
      background: colors.cellBg,
      border: `1px solid ${colors.cellBdr}`,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      color: colors.textMuted, marginBottom: '18px',
    }}>
      {icon}
    </div>
    <h2 style={{
      margin: 0, fontSize: '18px', fontWeight: 600,
      color: colors.text, letterSpacing: '-0.01em', marginBottom: '8px',
    }}>
      {title}
    </h2>
    <p style={{
      fontSize: '13px', color: colors.textSec, lineHeight: 1.6,
      maxWidth: '380px', margin: '0 auto 18px',
    }}>
      {body}
    </p>
    {action && (
      <button
        onClick={action.onClick}
        style={{
          padding: '8px 16px',
          background: 'transparent',
          color: colors.accent,
          border: `1px solid ${colors.accent}55`,
          borderRadius: '8px',
          fontSize: '13px', fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        {action.label}
      </button>
    )}
  </div>
);

// ============================================================================
// THEME TOKENS
// ============================================================================

const useColors = (isDark) => useMemo(() => isDark
  ? {
      text:        '#E7EAF0',
      textSec:     '#B8BDC9',
      textMuted:   '#6B7180',
      cellBg:      'rgba(255,255,255,0.02)',
      cellBdr:     'rgba(255,255,255,0.06)',
      modalBg:     '#15181D',
      btnBg:       'rgba(255,255,255,0.04)',
      btnBgHover:  'rgba(255,255,255,0.06)',
      inputBg:     'rgba(0,0,0,0.2)',
      accent:      '#38B6FF',
      accentDeep:  '#1B6BE0',
      accentSoft:  'rgba(56,182,255,0.12)',
    }
  : {
      text:        '#15181D',
      textSec:     '#3A3F4A',
      textMuted:   '#6B7180',
      cellBg:      'rgba(0,0,0,0.02)',
      cellBdr:     'rgba(0,0,0,0.07)',
      modalBg:     '#FFFFFF',
      btnBg:       'rgba(0,0,0,0.03)',
      btnBgHover:  'rgba(0,0,0,0.06)',
      inputBg:     '#FFFFFF',
      accent:      '#1B6BE0',
      accentDeep:  '#1453B0',
      accentSoft:  'rgba(27,107,224,0.08)',
    }, [isDark]);

export default AudioAudit;